import axios, { type AxiosError } from 'axios';
import {
  normalizeAttrDef as sharedNormalizeAttrDef,
  toRequestAllowedServiceTypeKeys,
  serviceSetsToServiceTypeKeys,
} from '@miso/counsel-schema';
import { externalApiClient } from '@/shared/lib/apiClient';
import type {
  AttrDef,
  AttributeCondition,
  CompileResult,
  DisplayTemplateRule,
  Spec,
} from '../model/moving-item.types';

const API_BASE = '/api/v1/admin/moving-item-specifications';

/** 목록 API 기본 페이지 크기 (서버 기본 20건 제한을 넘기 위해 명시) */
const DEFAULT_FETCH_SPECS_PAGE_SIZE = 100;
/** 커서 페이징 시 무한 루프 방지 */
const MAX_FETCH_SPECS_PAGES = 50;

interface FetchSpecsResponse {
  items?: Spec[];
  specs?: Spec[];
  nextCursor?: string | null;
}

/**
 * 서버 SERVICE_TYPE_CONSTRAINT 효과는 개별 서비스 키(`allowedServiceTypeKeys`)로 오고,
 * 편집기도 같은 단위를 쓰므로 그대로 둔다. 조합(`allowedServiceSets`)만 담긴 과거
 * 초안이 섞여 들어오면 개별 서비스로 환산해 편집 단위를 하나로 맞춘다.
 */
function normalizeConditions(raw: unknown): AttributeCondition[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return (raw as AttributeCondition[]).filter(Boolean).map((cond) => {
    const effect = cond.effect;
    if (effect?.type !== 'SERVICE_TYPE_CONSTRAINT') return cond;
    if (effect.allowedServiceTypeKeys?.length || !effect.allowedServiceSets?.length) return cond;
    return {
      ...cond,
      effect: {
        ...effect,
        allowedServiceSets: undefined,
        allowedServiceTypeKeys: serviceSetsToServiceTypeKeys(effect.allowedServiceSets),
      },
    };
  });
}

/**
 * 서버가 ENUM을 type: { type, values, enumDisplayNames } 형태로 반환할 때 정규화.
 *
 * 정규화 규칙 자체는 공유 패키지에 있다 — 짐 상세 플로우를 만드는 쪽(문서 런타임)과
 * 여기가 서로 다른 모양을 기대하면 열거형 질문이 조용히 텍스트 입력으로 떨어진다.
 */
function normalizeAttrDef(raw: Record<string, unknown>): AttrDef {
  const attr = sharedNormalizeAttrDef(raw) as AttrDef;
  return { ...attr, conditions: normalizeConditions(attr.conditions) };
}

function normalizeDisplayTemplateRules(raw: unknown): DisplayTemplateRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(Boolean) as DisplayTemplateRule[];
}

function normalizeSpec(spec: Record<string, unknown>): Spec {
  const attrs = spec.attributes;
  const normalizedAttrs = Array.isArray(attrs)
    ? attrs.map((a) =>
        normalizeAttrDef(typeof a === 'object' && a ? (a as Record<string, unknown>) : {})
      )
    : [];
  const displayTemplateRules = normalizeDisplayTemplateRules(spec.displayTemplateRules);
  const conditions = normalizeConditions(spec.conditions) ?? [];

  return {
    ...spec,
    attributes: normalizedAttrs,
    conditions,
    displayTemplateRules,
    ...('displayNameTemplate' in spec
      ? {
          displayNameTemplate:
            spec.displayNameTemplate === null
              ? null
              : spec.displayNameTemplate === undefined
                ? undefined
                : String(spec.displayNameTemplate as string | number | boolean),
        }
      : {}),
  } as Spec;
}

export interface FetchSpecsParams {
  keyword?: string;
  attributeKey?: string;
  /** 해당 방 타입이 roomTypes 에 포함된 품목만 반환 */
  roomType?: string;
  pageSize?: number;
  cursor?: string;
}

async function fetchSpecs(params?: FetchSpecsParams): Promise<Spec[]> {
  const pageSize = params?.pageSize ?? DEFAULT_FETCH_SPECS_PAGE_SIZE;
  const all: Spec[] = [];

  try {
    const headers = { 'Content-Type': 'application/json' };
    let cursor: string | null | undefined;

    for (let page = 0; page < MAX_FETCH_SPECS_PAGES; page++) {
      const query: Record<string, string | number> = { pageSize };
      if (params?.keyword) query.keyword = params.keyword;
      if (params?.attributeKey) query.attributeKey = params.attributeKey;
      if (params?.roomType) query.roomType = params.roomType;
      if (cursor) query.cursor = cursor;

      const response = await externalApiClient.get<FetchSpecsResponse>(API_BASE, {
        headers,
        params: query,
      });
      const rawItems = response.data.items ?? response.data.specs ?? [];
      for (const s of rawItems) {
        all.push(normalizeSpec(s as unknown as Record<string, unknown>));
      }

      const next = response.data.nextCursor;
      if (!next || rawItems.length === 0) break;
      cursor = next;
    }

    return all;
  } catch (error) {
    throwMovingItemRequestFailed(error, '이사 품목 스펙 목록 조회에 실패했습니다.');
  }
}

async function getSpec(specId: string): Promise<Spec> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.get<Spec>(`${API_BASE}/${specId}`, { headers });
    return normalizeSpec(response.data as unknown as Record<string, unknown>);
  } catch (error) {
    throwMovingItemRequestFailed(error, '이사 품목 스펙 조회에 실패했습니다.');
  }
}

/**
 * 저장 시 편집기 전용 필드를 제거하고, 서비스 제한 효과를 서버 표현으로 맞춘다.
 *
 * 서버 `EffectRequest` 는 허용 목록(`allowedServiceTypeKeys`)만 읽는다 — 조합(ServiceSet)
 * 이름을 그대로 보내면 제약이 빈 집합으로 저장되어 "전부 불가" 가 된다. 편집기는 불가
 * 목록(블랙리스트)을 다루므로 여기서 허용 목록으로 되돌린다.
 */
function stripConditionClientKeys(conditions: AttributeCondition[]): AttributeCondition[] {
  return conditions.map((c) => {
    const { _clientRowKey: _k, ...rest } = c;
    if (rest.effect?.type !== 'SERVICE_TYPE_CONSTRAINT') return rest;
    return {
      ...rest,
      effect: {
        ...rest.effect,
        allowedServiceSets: undefined,
        allowedServiceTypeKeys: toRequestAllowedServiceTypeKeys(rest.effect),
      },
    };
  });
}

function stripSpecClientKeys(spec: Omit<Spec, 'id'>): Omit<Spec, 'id'> {
  const displayTemplateRules =
    spec.displayTemplateRules === undefined
      ? undefined
      : spec.displayTemplateRules.map((row) => {
          const { _clientRowKey: _k, ...rest } = row;
          return rest;
        });
  return {
    ...spec,
    ...(displayTemplateRules !== undefined ? { displayTemplateRules } : {}),
    ...(spec.conditions !== undefined
      ? { conditions: stripConditionClientKeys(spec.conditions) }
      : {}),
    attributes: spec.attributes.map((attr) => ({
      ...attr,
      conditions: attr.conditions ? stripConditionClientKeys(attr.conditions) : undefined,
    })),
  };
}

function throwMovingItemRequestFailed(error: unknown, fallback: string): never {
  if (!axios.isAxiosError(error)) {
    throw new Error('알 수 없는 오류가 발생했습니다.');
  }
  throw new Error(extractMovingItemAxiosErrorMessage(error, fallback));
}

function extractMovingItemAxiosErrorMessage(error: AxiosError, fallback: string): string {
  const data = error.response?.data as Record<string, unknown> | undefined;
  const nestedError =
    data?.error && typeof data.error === 'object'
      ? (data.error as Record<string, unknown>)
      : undefined;
  const code =
    (typeof data?.code === 'string' && data.code) ||
    (typeof data?.errorCode === 'string' && data.errorCode) ||
    (typeof nestedError?.code === 'string' && nestedError.code) ||
    undefined;
  const message =
    (typeof data?.message === 'string' && data.message) ||
    (typeof nestedError?.message === 'string' && nestedError.message) ||
    error.message ||
    fallback;

  if (code === 'MOVING_009' || code === 'MOVING_ITEM_DISPLAY_TEMPLATE_RULES_INVALID') {
    const prefix = '표시명 규칙이 올바르지 않습니다.';
    return message && message !== fallback ? `${prefix} ${message}` : prefix;
  }
  if (code === 'MOVING_010' || code === 'MOVING_ITEM_SPEC_CONDITIONS_INVALID') {
    const prefix =
      '스펙 공통 규칙이 올바르지 않습니다. 평가 속성을 지정하거나 "항상 참"으로 두세요.';
    return message && message !== fallback ? `${prefix} ${message}` : prefix;
  }
  return message;
}

function ensureDescription(spec: Omit<Spec, 'id'>): Omit<Spec, 'id'> {
  return stripSpecClientKeys({
    ...spec,
    description: spec.description ?? '',
  });
}

async function createSpec(spec: Omit<Spec, 'id'>): Promise<Spec> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const payload = ensureDescription(spec);
    const response = await externalApiClient.post<Spec>(API_BASE, payload, { headers });
    return normalizeSpec(response.data as unknown as Record<string, unknown>);
  } catch (error) {
    throwMovingItemRequestFailed(error, '이사 품목 스펙 생성에 실패했습니다.');
  }
}

async function updateSpec(specId: string, spec: Omit<Spec, 'id'>): Promise<Spec> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const payload = ensureDescription(spec);
    const response = await externalApiClient.put<Spec>(`${API_BASE}/${specId}`, payload, {
      headers,
    });
    return normalizeSpec(response.data as unknown as Record<string, unknown>);
  } catch (error) {
    throwMovingItemRequestFailed(error, '이사 품목 스펙 수정에 실패했습니다.');
  }
}

function normalizeCompileResponsePayload(
  data: CompileResult & Record<string, unknown>
): CompileResult {
  const compiled =
    data.compiled && typeof data.compiled === 'object'
      ? (data.compiled as { displayName?: string })
      : undefined;

  const resolvedDisplayName: string | null | undefined = (() => {
    const tryStr = (v: unknown) => (typeof v === 'string' ? v : undefined);
    return (
      tryStr(data.resolvedDisplayName) ??
      tryStr(data.displayName) ??
      tryStr(data.movingItemDisplayName) ??
      tryStr(data.resolved_display_name) ??
      (compiled?.displayName !== undefined ? String(compiled.displayName) : undefined)
    );
  })();

  return {
    ...data,
    ...(resolvedDisplayName !== undefined ? { resolvedDisplayName } : {}),
  };
}

async function compile(specId: string, attributes: Record<string, string>): Promise<CompileResult> {
  try {
    const headers = { 'Content-Type': 'application/json' };
    const response = await externalApiClient.post<CompileResult>(
      `${API_BASE}/${specId}/compile`,
      { attributes },
      { headers }
    );
    return normalizeCompileResponsePayload(
      response.data as CompileResult & Record<string, unknown>
    );
  } catch (error) {
    throwMovingItemRequestFailed(error, '이사 품목 컴파일에 실패했습니다.');
  }
}

export const movingItemApi = {
  fetchSpecs,
  getSpec,
  createSpec,
  updateSpec,
  compile,
};
